import apiClient, { API_BASE_URL } from "@services/api/apiClient.jsx";

/**
 * 리소스 관련 API 서비스
 */
const resourceApi = {
    /**
     * 파일 업로드
     * @param {File} file 업로드할 파일
     * @param {string} description 파일 설명 (선택사항)
     * @returns {Promise<Object>} 업로드된 리소스 정보
     */
    uploadFile: async (file, description = '') => {
        try {
            const formData = new FormData();
            formData.append('file', file);
            
            if (description) {
                formData.append('description', description);
            }
            
            const response = await apiClient.post('/resource', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            
            return response.data;
        } catch (error) {
            console.error('파일 업로드 실패:', error);
            throw error;
        }
    },

    /**
     * 리소스 정보 조회
     * @param {string} id 리소스 UUID
     * @returns {Promise<Object>} 리소스 정보
     */
    getResource: async (id) => {
        try {
            const response = await apiClient.get(`/resource/${id}`);
            return response.data;
        } catch (error) {
            console.error(`리소스 ID ${id} 정보 조회 실패:`, error);
            throw error;
        }
    },

    /**
     * 리소스 뷰 URL 생성 (이미지 직접 표시용)
     * @param {string} id 리소스 UUID
     * @returns {string} 리소스 뷰 URL
     */
    getViewUrl: (id) => {
        if (!id) return '';
        return `${API_BASE_URL}/resource/view/${id}`;
    },

    /**
     * 리소스 다운로드 URL 생성
     * @param {string} id 리소스 UUID
     * @returns {string} 리소스 다운로드 URL
     */
    getDownloadUrl: (id) => {
        if (!id) return '';
        return `${API_BASE_URL}/resource/download/${id}`;
    },

    /**
     * 리소스 정보 업데이트
     * @param {string} id 리소스 UUID
     * @param {Object} updateData 업데이트할 데이터
     * @returns {Promise<Object>} 업데이트된 리소스 정보
     */
    updateResource: async (id, updateData) => {
        try {
            const response = await apiClient.put(`/resource/${id}`, updateData);
            return response.data;
        } catch (error) {
            console.error(`리소스 ID ${id} 정보 업데이트 실패:`, error);
            throw error;
        }
    },

    /**
     * 리소스 삭제
     * @param {string} id 리소스 UUID
     * @returns {Promise<void>}
     */
    deleteResource: async (id) => {
        try {
            await apiClient.delete(`/resource/${id}`);
            return true;
        } catch (error) {
            console.error(`리소스 ID ${id} 삭제 실패:`, error);
            throw error;
        }
    },

    /**
     * 리소스 목록 조회 (페이징)
     * @param {number} page 페이지 번호 (0부터 시작)
     * @param {number} size 페이지 크기
     * @returns {Promise<Object>} 페이징된 리소스 목록
     */
    listResources: async (page = 0, size = 20) => {
        try {
            const response = await apiClient.get(`/resource?page=${page}&size=${size}`);
            return response.data;
        } catch (error) {
            console.error('리소스 목록 조회 실패:', error);
            throw error;
        }
    },

    /**
     * 파일명으로 리소스 검색
     * @param {string} filename 검색할 파일명
     * @param {number} page 페이지 번호 (0부터 시작)
     * @param {number} size 페이지 크기
     * @returns {Promise<Object>} 페이징된 검색 결과
     */
    searchByFilename: async (filename, page = 0, size = 20) => {
        try {
            const response = await apiClient.get(`/resource/search/filename?filename=${encodeURIComponent(filename)}&page=${page}&size=${size}`);
            return response.data;
        } catch (error) {
            console.error(`파일명 '${filename}' 검색 실패:`, error);
            throw error;
        }
    },

    /**
     * 콘텐츠 타입으로 리소스 검색
     * @param {string} contentType 검색할 콘텐츠 타입 (예: image/jpeg)
     * @param {number} page 페이지 번호 (0부터 시작)
     * @param {number} size 페이지 크기
     * @returns {Promise<Object>} 페이징된 검색 결과
     */
    searchByContentType: async (contentType, page = 0, size = 20) => {
        try {
            const response = await apiClient.get(`/resource/search/content-type?contentType=${encodeURIComponent(contentType)}&page=${page}&size=${size}`);
            return response.data;
        } catch (error) {
            console.error(`콘텐츠 타입 '${contentType}' 검색 실패:`, error);
            throw error;
        }
    }
};

export default resourceApi;